package com.eladfinish.bible.contest.screens.lexicographicalSort

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.common.ChaptersRepository
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.domain.BooksRepository
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.OutputChapters
import com.eladfinish.bible.contest.screens.mikud.MikudRepository
import com.eladfinish.bible.contest.screens.mikud.usecases.GetChaptersInMikudUseCase
import com.eladfinish.bible.contest.verses_per_chapter_csv

/**
 * Resolves how many verses the generated lexicographical table will contain, without
 * actually parsing the chapter files.
 *
 * The verse count per chapter is read once from `verses_per_chapter.csv` (a static asset
 * holding one `filename,verses` row per chapter, where `filename` matches
 * [ChaptersRepository.getFileFormat]). The total is then:
 *  - [OutputChapters.SINGLE]: the single selected chapter.
 *  - [OutputChapters.ALL]: the sum of every chapter in the book.
 *  - [OutputChapters.BY_MIKUD]: the sum of the chapters included in the current mikud.
 */
class GetVerseCountUseCase(
    private val assetReader: AssetReader,
    private val mikudRepository: MikudRepository,
    private val getChaptersInMikudUseCase: GetChaptersInMikudUseCase,
) {

    /** Map of chapter file name (e.g. "_1_bereshit-01.txt") to its verse count. */
    private val versesByFilename: Map<String, Int> by lazy { parseCsv() }

    operator fun invoke(
        bookNumber: Int,
        chapterNumber: Int,
        outputChapters: OutputChapters,
    ): Int = when (outputChapters) {
        OutputChapters.SINGLE -> versesInChapter(bookNumber = bookNumber, chapterNumber = chapterNumber)

        OutputChapters.ALL -> {
            val numOfChapters = BooksRepository.numOfChaptersPerBook[bookNumber - 1]
            (1..numOfChapters).sumOf { versesInChapter(bookNumber = bookNumber, chapterNumber = it) }
        }

        OutputChapters.BY_MIKUD ->
            getChaptersInMikudUseCase(mikudStr = mikudRepository.mikudList, bookNumber = bookNumber)
                .sumOf { versesByFilename[it.second] ?: 0 }
    }

    /** The verses of one chapter file (e.g. "_1_bereshit-01.txt"); 0 for a file the table does not know. */
    fun inChapterFile(filename: String): Int = versesByFilename[filename] ?: 0

    private fun versesInChapter(bookNumber: Int, chapterNumber: Int): Int {
        val bookNameEnglish = BooksRepository.bookNamesEnglish[bookNumber - 1]
        val paddedNum = ChaptersRepository.padNumber(bookNameEnglish, chapterNumber)
        val filename = ChaptersRepository.getFileFormat(
            number = bookNumber, bookName = bookNameEnglish, chapter = paddedNum,
        )
        return versesByFilename[filename] ?: 0
    }

    private fun parseCsv(): Map<String, Int> {
        val csv = assetReader.readFile(SharedRes.assets.verses_per_chapter_csv)
        return csv.lineSequence()
            .drop(1) // header: filename,verses
            .mapNotNull { line ->
                val parts = line.split(",")
                if (parts.size < 2) return@mapNotNull null
                val filename = parts[0].trim()
                val count = parts[1].trim().toIntOrNull() ?: return@mapNotNull null
                if (filename.isEmpty()) null else filename to count
            }
            .toMap()
    }
}
